import { COUNTRIES_MAP } from '@/const/countries.const';
import { normalizeText } from '@/domain/pos/search-normalize';
import type { IPlaceSuggestion } from '@/interfaces/IStoreSetup';
import type { TSetupPinSource } from '@/composition/store-setup/use-store-setup-draft';
import { regionName } from './display-names';
import { SUPPORTED_COUNTRIES } from './store-country';

/**
 * Pure rules of «Ubicación» (plan §4.1): pin validity (D6), result groups,
 * the country list, the address prefill and the bottom-bar helper.
 */

/** A manual pin counts only from this zoom (street level). */
export const PIN_MIN_ZOOM = 15;
/** Zoom of a picked place, a geocoded address or the device position. */
export const PLACE_ZOOM = 17;
/** Zoom of the country overview (manual pin mode without an address). */
export const COUNTRY_ZOOM = 5;
export const MAX_RESULTS_PER_GROUP = 5;
export const ADDRESS_MIN = 3;
export const ADDRESS_MAX = 200;
export const REFERENCE_MAX = 120;

export interface ILatLng {
  lat: number;
  lng: number;
}

/** 6 decimals (≈ 11 cm): what the pin stores. */
export function roundCoord(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/**
 * D6: a place, a geocoded address or the device position are valid at once;
 * a manual pin only after the map was moved at least once to street zoom.
 */
export function isPinValid(
  source: TSetupPinSource | null,
  moved: boolean,
  zoom: number
): boolean {
  if (source === null) return false;
  if (source !== 'manual') return true;
  return moved && zoom >= PIN_MIN_ZOOM;
}

export interface ISuggestionGroups {
  businesses: IPlaceSuggestion[];
  addresses: IPlaceSuggestion[];
}

/** «Negocios» then «Direcciones», each in Google's order, at most 5 rows. */
export function groupSuggestions(
  results: readonly IPlaceSuggestion[]
): ISuggestionGroups {
  return {
    businesses: results
      .filter((result) => result.kind === 'business')
      .slice(0, MAX_RESULTS_PER_GROUP),
    addresses: results
      .filter((result) => result.kind === 'address')
      .slice(0, MAX_RESULTS_PER_GROUP),
  };
}

export interface IPickerOption {
  value: string;
  label: string;
}

/** The 16 supported countries named in the UI locale, sorted by that locale. */
export function countryOptions(locale: string): IPickerOption[] {
  return SUPPORTED_COUNTRIES.map((code) => ({
    value: code,
    label: regionName(code, locale),
  })).sort((a, b) => a.label.localeCompare(b.label, locale));
}

/** Options whose label contains the query, ignoring case, accents and extra spaces. */
export function filterPickerOptions<T extends IPickerOption>(
  options: readonly T[],
  query: string
): T[] {
  const needle = normalizeText(query);
  if (!needle) return [...options];
  return options.filter((option) =>
    normalizeText(option.label).includes(needle)
  );
}

/** Names Google may print for a country besides the `Intl` ones. */
const COUNTRY_ALIASES: Readonly<Record<string, readonly string[]>> = {
  US: ['USA', 'EE. UU.', 'EUA', 'United States of America'],
};

function comparableName(text: string): string {
  return normalizeText(text).replace(/[.\s]/g, '');
}

function countryNames(country: string, locale: string): Set<string> {
  const names = [locale, 'es', 'en', 'pt'].map((lang) =>
    regionName(country, lang)
  );
  const listed = COUNTRIES_MAP.get(country)?.text;
  if (listed) names.push(listed);
  names.push(...(COUNTRY_ALIASES[country] ?? []));
  return new Set(names.map(comparableName));
}

/**
 * The address customers see: Google's formatted address without its trailing
 * country («Av. Corrientes 1234, CABA, Argentina» → «Av. Corrientes 1234,
 * CABA»), cut to the field's 200 characters.
 */
export function addressWithoutCountry(
  formatted: string,
  country: string | null,
  locale: string
): string {
  const parts = formatted.split(',');
  const trimmed = formatted.trim();
  if (!country || parts.length < 2) return trimmed.slice(0, ADDRESS_MAX).trim();
  const last = comparableName(parts[parts.length - 1]);
  const withoutCountry = countryNames(country, locale).has(last)
    ? parts.slice(0, -1).join(',').trim()
    : trimmed;
  return withoutCountry.slice(0, ADDRESS_MAX).trim();
}

/** Places (New) details of a business carry `establishment`; addresses do not. */
export function isBusinessPlace(types: readonly string[]): boolean {
  return types.includes('establishment');
}

export type TLocationMethod =
  | 'business'
  | 'address'
  | 'manual'
  | 'geolocation'
  | 'geocode';

/** The `location_confirmed` analytics method (§4.13). */
export function locationMethod(
  source: TSetupPinSource,
  types: readonly string[]
): TLocationMethod {
  if (source === 'place')
    return isBusinessPlace(types) ? 'business' : 'address';
  return source;
}

export interface ILocationHelperInput {
  pinVisible: boolean;
  pinValid: boolean;
  source: TSetupPinSource | null;
  moved: boolean;
  zoom: number;
  addressValid: boolean;
}

/** The bottom-bar helper of «Ubicación»: the first thing still missing. */
export function locationHelperKey(input: ILocationHelperInput): string {
  if (input.pinValid)
    return input.addressValid
      ? 'storeSetup.location.helperPicked'
      : 'storeSetup.location.addressRequired';
  if (input.pinVisible && input.source === 'manual')
    return input.moved && input.zoom < PIN_MIN_ZOOM
      ? 'storeSetup.location.pinNeedsZoom'
      : 'storeSetup.location.helperMovePin';
  return 'storeSetup.location.helperSearch';
}

/** The country centre of `COUNTRIES` (never saved as a pin, D6). */
export function countryCentre(country: string): ILatLng | null {
  const entry = COUNTRIES_MAP.get(country);
  return entry ? { lat: entry.lat, lng: entry.lon } : null;
}
